import { Checkbox, Input, message, Modal, Space, Typography } from 'antd';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as THREE from 'three';
import AuthService from '../../../api/auth';
import { BASE_API_URL } from '../../../config';

import { ReactComponent as BackArrowIcon } from '../../../assets/svgs/back-arrow-icon.svg';

import { RoutePath } from '../../../components/views/AppRoot/types';
import { StoreBuildModel } from '../../../models/store-build';
import { SELECTION_COLOR, SELECTION_OPACITY } from './constants';
import { FebViewProps, ShopperMesh, ShopperType } from './types';

import { useAppDispatch, useAppSelector } from '../../../store';
import { clearCurrentStoreBuild } from '../../../store/features/storeBuilds/storeBuildsSlice';

import SidePanel from './components/SidePanel/SidePanel';
import Toolbar from './components/Toolbar/Toolbar';

import { environmentsByIdMap } from '../StoreBuilderDetailsView/environments';
import {
  alignAdjacent,
  alignAdjacentVertical,
  alignBottom,
  alignHorizontally,
  alignLeft,
  alignRight,
  alignTop,
  alignVertically,
} from './alignmentOperations';
import {
  addSelectionOverlay,
  removeSelectionOverlay,
  updateShopperBadgeSelection,
} from './meshOperations';
import { setupThreeScene } from './threeSetup';

import { cameraManager } from '../../../services/CameraManager';
import { meshFactory } from '../../../services/MeshFactory';
import { queueLineManager } from '../../../services/QueueLineManager';
import { sceneManager } from '../../../services/SceneManager';
import './FebView.less';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useKeyboardInteractions } from './hooks/useKeyboardInteractions';
import { useMouseInteractions } from './hooks/useMouseInteractions';
import { useQueueLineState } from './hooks/useQueueLineState';
import { useSelectionState } from './hooks/useSelectionState';
import { useThreeRefs } from './hooks/useThreeRefs';
import { useUIState } from './hooks/useUIState';
// Queue line operations now handled by queueLineManager service
import { snappingService } from '../../../services/SnappingService';
import ContextMenu, { MenuItem } from './components/ContextMenu/ContextMenu';
import { PerformanceDebugPanel } from './components/PerformanceDebugPanel';
import SelectionBoxOverlay from './components/SelectionBoxOverlay';
import { SnappingDebugPanel } from './components/SnappingDebugPanel';
import { useContextMenu } from './hooks/useContextMenu';
import { useSelectionBox } from './hooks/useSelectionBox';

const { Title } = Typography;

const createDebouncedCheckOverlaps = (
  setOverlappingShoppers: (shoppers: string[]) => void
) => {
  return debounce((scene: THREE.Scene) => {
    const overlapping = queueLineManager.checkForOverlappingShoppers(scene);
    setOverlappingShoppers(overlapping);
  }, 100);
};

const EDITING_SELECTION_OPACITY = 0.3; // Higher opacity for active editing

const FebView: FC<FebViewProps> = ({ build: propsBuild }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { currentStoreBuild } = useAppSelector((state) => state.storeBuilds);

  const {
    selectedMeshesForUI,
    setSelectedMeshesForUI,
    activeTextMesh,
    setActiveTextMesh,
    selectedQueueLine,
    setSelectedQueueLine,
    overlappingShoppers,
    setOverlappingShoppers,
  } = useSelectionState();

  const {
    isShiftPressed,
    setIsShiftPressed,
    isGridVisible,
    setIsGridVisible,
    currentRenderType,
    setCurrentRenderType,
    isCameraControlsEnabled,
    setIsCameraControlsEnabled,
    showCameraControlsModal,
    setShowCameraControlsModal,
    dontShowAgain,
    setDontShowAgain,
    cubeSize,
    setCubeSize,
    sphereRadius,
    setSphereRadius,

    // Debug mode
    isDebugMode,
    setIsDebugMode,
  } = useUIState();

  const {
    isInQueueLineMode,
    setIsInQueueLineMode,
    queueLines,
    setQueueLines,
    currentQueueLine,
    setCurrentQueueLine,
    selectedShopperType,
    setSelectedShopperType,
  } = useQueueLineState();

  const textInputRef = useRef<HTMLInputElement>(null);

  const debouncedCheckOverlaps = useMemo(
    () => createDebouncedCheckOverlaps(setOverlappingShoppers),
    [setOverlappingShoppers]
  );

  const mountRef = useRef<HTMLDivElement>(null);
  const refs = useThreeRefs();

  const cleanupAllSelectionOverlays = useCallback(() => {
    if (!refs.scene.current) return;

    // Find and remove all selection overlays
    const overlays = refs.scene.current.children.filter(
      (obj) => obj instanceof THREE.Mesh && obj.userData.isSelectionOverlay
    );

    overlays.forEach((overlay) => {
      refs.scene.current!.remove(overlay);
      // Optionally, if the original mesh that this overlay was for needs its userData.hasOverlay cleared,
      // that logic would go here, or be part of a more comprehensive removeSelectionOverlay(originalMesh, scene) call.
    });

    // DO NOT CLEAR SELECTION STATE HERE
    // refs.selectedMeshes.current = [];
    // setSelectedMeshesForUI([]);
    // setSelectedQueueLine(null);
  }, [refs.scene]); // Removed setSelectedMeshesForUI and setSelectedQueueLine from dependencies

  const build = useMemo(() => {
    if (propsBuild) return propsBuild;

    const locationState = location.state as
      | { data?: StoreBuildModel }
      | undefined;
    if (locationState?.data) return locationState.data;

    return currentStoreBuild;
  }, [propsBuild, location.state, currentStoreBuild]);

  const environment = useMemo(
    () => build && environmentsByIdMap[build.feb_design_area_id],
    [build]
  );

  useEffect(() => {
    if (!mountRef.current || !build || !environment) return;

    const cleanup = setupThreeScene(mountRef.current, refs, environment);

    // Configure OrbitControls for free movement
    if (refs.controls.current) {
      cameraManager.configureCameraControls(refs.controls.current, {
        enableRotate: true,
        enablePan: true,
        enableZoom: true,
        rotateSpeed: 1.0,
        zoomSpeed: 1.2,
        panSpeed: 0.8,
        minPolarAngle: 0, // Top-down view
        maxPolarAngle: Math.PI / 2, // Horizontal view, prevents seeing underneath
        minDistance: 0.1,
        maxDistance: 100,
      });
    }

    return cleanup;
  }, [refs, environment, build]);

  useEffect(() => {
    if (!refs.scene.current || !refs.floorPlane.current) return;

    // Get the center of the floor
    const floorBounds = new THREE.Box3().setFromObject(
      refs.floorPlane.current!
    );
    const floorCenter = new THREE.Vector3();
    floorBounds.getCenter(floorCenter);

    // Add lights
    const light = new THREE.PointLight(0xffffff, 1, 100);
    light.position.set(floorCenter.x, 5, floorCenter.z);
    refs.scene.current.add(light);

    const ambientLight = new THREE.AmbientLight(0x404040);
    refs.scene.current.add(ambientLight);
  }, [refs.scene, refs.floorPlane]);

  const clearSelections = useCallback(() => {
    if (!refs.scene.current) return;

    const meshesWithOverlays = refs.scene.current.children.filter(
      (obj) => obj instanceof THREE.Mesh && obj.userData.hasOverlay
    );

    meshesWithOverlays.forEach((mesh) => {
      removeSelectionOverlay(mesh as THREE.Mesh, refs.scene.current!);

      if (mesh.userData.isQueueLineShopper) {
        updateShopperBadgeSelection(mesh as ShopperMesh, false);
      }
    });

    refs.selectedMeshes.current = [];
    setSelectedMeshesForUI([]);

    // Update alignment lines when selections are cleared
    snappingService.updateAlignmentLines(refs.scene.current);
  }, [refs.scene, refs.selectedMeshes, setSelectedMeshesForUI]);

  // Handle drag and drop using custom hook
  const { setDraggedItem } = useDragAndDrop({
    mountRef,
    refs,
    build,
    environment,
    clearSelections,
    setSelectedMeshesForUI,
  });

  // Handle mouse interactions using custom hook
  useMouseInteractions({
    mountRef,
    refs,
    build,
    environment,
    isShiftPressed,
    isCameraControlsEnabled,
    clearSelections,
    setSelectedMeshesForUI,
    debouncedCheckOverlaps,
  });

  // Handle keyboard interactions using custom hook
  useKeyboardInteractions({
    build,
    environment,
    refs,
    isShiftPressed,
    setIsShiftPressed,
    activeTextMesh,
    setActiveTextMesh,
    textInputRef,
    setQueueLines,
    cleanupAllSelectionOverlays,
    isDebugMode,
    setIsDebugMode,
    // Performance panel toggle handler
    onTogglePerformancePanel: () => {
      setShowPerformancePanel((prev) => !prev);
    },
  });

  const handleZoomIn = useCallback(() => {
    if (refs.camera.current) {
      cameraManager.zoomIn(
        refs.camera.current,
        refs.controls.current || undefined
      );
    }
  }, [refs.camera, refs.controls]);

  const handleZoomOut = useCallback(() => {
    if (refs.camera.current) {
      cameraManager.zoomOut(
        refs.camera.current,
        refs.controls.current || undefined
      );
    }
  }, [refs.camera, refs.controls]);

  const handleZoomToFit = useCallback(() => {
    if (
      !refs.camera.current ||
      !refs.floorPlane.current ||
      !refs.controls.current ||
      !mountRef.current
    )
      return;

    cameraManager.zoomToFit(
      refs.camera.current,
      refs.controls.current,
      refs.floorPlane.current,
      mountRef.current
    );
  }, [refs.camera, refs.floorPlane, refs.controls, mountRef]);

  const handleResetCamera = useCallback(() => {
    if (!refs.camera.current || !refs.controls.current) return;

    cameraManager.resetCamera(refs.camera.current, refs.controls.current);
  }, [refs.camera, refs.controls]);

  const handleRotate = useCallback(() => {
    if (!refs.selectedMeshes.current.length) return;

    refs.selectedMeshes.current.forEach((mesh) => {
      if (mesh.userData.isQueueLineShopper) {
        // For shoppers, rotate around Y axis
        const newRotation = mesh.rotation.y + Math.PI / 2;
        mesh.rotation.y = newRotation;
      } else {
        // For other objects, rotate around Z axis
        const newRotation = mesh.rotation.z + Math.PI / 2;
        mesh.rotation.z = newRotation;
      }

      if (mesh.userData.hasOverlay && refs.scene.current) {
        const overlayUuid = mesh.userData.hasOverlay;
        const overlay = refs.scene.current.children.find(
          (obj) => obj.uuid === overlayUuid
        ) as THREE.Mesh;

        if (overlay) {
          if (mesh.userData.isQueueLineShopper) {
            overlay.rotation.y = mesh.rotation.y;
          } else {
            overlay.rotation.z = mesh.rotation.z;
          }
        }
      }
    });
  }, [refs.scene, refs.selectedMeshes]);

  const handleTakeScreenshot = useCallback(() => {
    if (
      !refs.renderer.current ||
      !refs.scene.current ||
      !refs.camera.current ||
      !build
    )
      return;

    cameraManager.takeScreenshot(
      refs.renderer.current,
      refs.scene.current,
      refs.camera.current,
      `${build.name}_screenshot.png`
    );
  }, [refs.renderer, refs.scene, refs.camera, build]);

  const handleDownloadRSF = async (buildId: string) => {
    try {
      const token = AuthService().getAuthToken();
      const response = await fetch(
        `${BASE_API_URL}/api/store-builds/${buildId}/rsf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to download RSF file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `build-${buildId}.rsf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      message.error('Failed to download RSF file');
      console.error('Error downloading RSF file:', error);
    }
  };

  const createTextMesh = (position: THREE.Vector3, text = 'Text') => {
    if (!refs.scene.current) {
      console.error('Scene not available');
      return null;
    }

    const textMesh = meshFactory.createTextMesh(position, text);
    if (textMesh) {
      refs.scene.current.add(textMesh);
    }
    return textMesh;
  };

  const updateTextMesh = (mesh: THREE.Mesh, newText: string) => {
    meshFactory.updateTextMesh(mesh, newText);
  };

  const positionTextInput = (
    mesh: THREE.Mesh,
    camera: THREE.Camera,
    mountElement: HTMLElement,
    inputElement: HTMLInputElement
  ) => {
    // Get the position of the text mesh in world space
    const position = new THREE.Vector3();
    mesh.getWorldPosition(position);

    // Project the 3D position to 2D screen space
    const screenPosition = position.clone();
    screenPosition.project(camera);

    // Convert to pixel coordinates
    const rect = mountElement.getBoundingClientRect();
    const x = ((screenPosition.x + 1) * rect.width) / 2 + rect.left;
    const y = ((-screenPosition.y + 1) * rect.height) / 2 + rect.top;

    // Position the input element
    inputElement.style.left = `${x}px`;
    inputElement.style.top = `${y}px`;
    inputElement.style.display = 'block';
    inputElement.value = mesh.userData.text || 'Text';
    inputElement.focus();
    inputElement.select();

    // Update selection overlay to indicate editing mode
    if (mesh.userData.hasOverlay && refs.scene.current) {
      const overlayUuid = mesh.userData.hasOverlay;
      const overlay = refs.scene.current.children.find(
        (obj) => obj.uuid === overlayUuid
      ) as THREE.Mesh;

      if (overlay && overlay.material instanceof THREE.MeshBasicMaterial) {
        overlay.material.opacity = EDITING_SELECTION_OPACITY;
        overlay.material.needsUpdate = true;
      }
    }
  };

  const handleAddText = useCallback(() => {
    if (
      !refs.scene.current ||
      !refs.floorPlane.current ||
      !refs.camera.current
    ) {
      console.error('Required refs not available');
      return;
    }

    // Get the center of the floor
    const floorBounds = new THREE.Box3().setFromObject(refs.floorPlane.current);
    const floorCenter = new THREE.Vector3();
    floorBounds.getCenter(floorCenter);

    // Create new text mesh
    const textMesh = createTextMesh(floorCenter);
    if (!textMesh) {
      console.error('Failed to create text mesh');
      return;
    }

    // Clear previous selections and select the new text mesh
    clearSelections();
    refs.selectedMeshes.current = [textMesh];
    setSelectedMeshesForUI([textMesh]);
    addSelectionOverlay(
      textMesh,
      refs.scene.current,
      SELECTION_COLOR,
      SELECTION_OPACITY
    );

    setActiveTextMesh(textMesh);

    // Position and show the input element
    if (textInputRef.current && mountRef.current) {
      positionTextInput(
        textMesh,
        refs.camera.current,
        mountRef.current,
        textInputRef.current
      );
    }
  }, [
    refs.scene,
    refs.floorPlane,
    refs.camera,
    refs.selectedMeshes,
    clearSelections,
  ]);

  // Update camera controls when toggle state changes
  useEffect(() => {
    if (!refs.controls.current) return;

    cameraManager.enableCameraControls(
      refs.controls.current,
      isCameraControlsEnabled
    );

    // If camera controls are enabled, disable any active dragging
    if (isCameraControlsEnabled && refs.isDraggingMesh.current) {
      refs.isDraggingMesh.current = false;
      refs.selectedMesh.current = null;
    }
  }, [
    isCameraControlsEnabled,
    refs.controls,
    refs.isDraggingMesh,
    refs.selectedMesh,
  ]);

  // Handle double click for text editing
  useEffect(() => {
    if (!mountRef.current) return;

    const handleDoubleClick = (event: MouseEvent) => {
      if (!refs.scene.current || !refs.camera.current) return;

      // Get normalized mouse coordinates
      const rect = mountRef.current!.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, refs.camera.current);

      // Find intersected objects
      const intersects = raycaster.intersectObjects(
        refs.scene.current.children
      );
      const textMesh = intersects.find(
        (intersect) => intersect.object.userData?.isText
      )?.object as THREE.Mesh;

      if (textMesh) {
        setActiveTextMesh(textMesh);

        // Position the input element using the helper function
        if (textInputRef.current && mountRef.current) {
          positionTextInput(
            textMesh,
            refs.camera.current,
            mountRef.current,
            textInputRef.current
          );
        }
      }
    };

    mountRef.current.addEventListener('dblclick', handleDoubleClick);
    return () => {
      mountRef.current?.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [refs.scene, refs.camera]);

  const handleTextInputBlur = () => {
    if (activeTextMesh && textInputRef.current) {
      updateTextMesh(activeTextMesh, textInputRef.current.value);
      textInputRef.current.style.display = 'none';

      // Reset selection overlay opacity
      if (activeTextMesh.userData.hasOverlay && refs.scene.current) {
        const overlayUuid = activeTextMesh.userData.hasOverlay;
        const overlay = refs.scene.current.children.find(
          (obj) => obj.uuid === overlayUuid
        ) as THREE.Mesh;

        if (overlay && overlay.material instanceof THREE.MeshBasicMaterial) {
          overlay.material.opacity = SELECTION_OPACITY;
          overlay.material.needsUpdate = true;
        }
      }

      setActiveTextMesh(null);
    }
  };

  const handleTextInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      textInputRef.current?.blur();
    }
  };

  // Update texture based on grid visibility and render type
  const updateFloorTexture = useCallback(
    async (showGrid: boolean, renderType: 'rendered' | 'lined') => {
      if (!refs.floorPlane.current || !environment) return;

      let textureSource: string;

      if (showGrid && renderType === 'rendered') {
        textureSource = environment.renders.renderedWithGrid;
      } else if (showGrid && renderType === 'lined') {
        textureSource = environment.renders.linedWithGrid;
      } else if (!showGrid && renderType === 'rendered') {
        textureSource = environment.renders.rendered;
      } else {
        textureSource = environment.renders.lined;
      }

      try {
        await sceneManager.updateFloorTexture(
          refs.floorPlane.current,
          textureSource
        );
      } catch (error) {
        console.error('Failed to update floor texture:', error);
      }
    },
    [refs.floorPlane, environment]
  );

  const toggleGridVisibility = useCallback(() => {
    const newIsGridVisible = !isGridVisible;
    setIsGridVisible(newIsGridVisible);

    updateFloorTexture(newIsGridVisible, currentRenderType);
  }, [isGridVisible, currentRenderType, updateFloorTexture]);

  useEffect(() => {
    if (environment && refs.floorPlane.current) {
      updateFloorTexture(isGridVisible, currentRenderType);
    }
  }, [
    environment,
    updateFloorTexture,
    isGridVisible,
    currentRenderType,
    refs.floorPlane,
  ]);

  const toggleRenderType = useCallback(() => {
    const newRenderType =
      currentRenderType === 'rendered' ? 'lined' : 'rendered';
    setCurrentRenderType(newRenderType);
    updateFloorTexture(isGridVisible, newRenderType);
  }, [currentRenderType, isGridVisible, updateFloorTexture]);

  const handleAddRectangle = useCallback(() => {
    if (!refs.scene.current || !refs.floorPlane.current) return;

    // Get the center of the floor
    const floorBounds = new THREE.Box3().setFromObject(refs.floorPlane.current);
    const floorCenter = new THREE.Vector3();
    floorBounds.getCenter(floorCenter);

    // Create a cube mesh using meshFactory
    const cube = meshFactory.createCubeMesh(floorCenter);

    // Add to scene and select it
    refs.scene.current.add(cube);
    clearSelections();
    refs.selectedMeshes.current = [cube];
    setSelectedMeshesForUI([cube]);
    addSelectionOverlay(
      cube,
      refs.scene.current,
      SELECTION_COLOR,
      SELECTION_OPACITY
    );

    // Update alignment lines after adding a new cube
    snappingService.updateAlignmentLines(refs.scene.current);
  }, [refs.scene, refs.floorPlane, refs.selectedMeshes, clearSelections]);

  const handleAddCircle = useCallback(() => {
    if (!refs.scene.current || !refs.floorPlane.current) return;

    // Get the center of the floor
    const floorBounds = new THREE.Box3().setFromObject(refs.floorPlane.current);
    const floorCenter = new THREE.Vector3();
    floorBounds.getCenter(floorCenter);

    // Create a sphere mesh using meshFactory
    const sphere = meshFactory.createSphereMesh(floorCenter);

    // Add to scene and select it
    refs.scene.current.add(sphere);
    clearSelections();
    refs.selectedMeshes.current = [sphere];
    setSelectedMeshesForUI([sphere]);
    addSelectionOverlay(
      sphere,
      refs.scene.current,
      SELECTION_COLOR,
      SELECTION_OPACITY
    );

    // Update alignment lines after adding a new sphere
    snappingService.updateAlignmentLines(refs.scene.current);
  }, [refs.scene, refs.floorPlane, refs.selectedMeshes, clearSelections]);

  const handleUpdateMesh = useCallback(
    (mesh: THREE.Mesh, updates: any) => {
      if (!refs.scene.current) return;

      // Use meshFactory to update geometry
      meshFactory.updateMeshGeometry(mesh, updates);

      // If there's a color update in userData, update the material color
      if (updates.userData?.originalColor) {
        meshFactory.updateMeshColor(mesh, updates.userData.originalColor);
      }

      // Update the selection overlay if it exists
      if (mesh.userData.hasOverlay) {
        const overlayUuid = mesh.userData.hasOverlay;
        const overlay = refs.scene.current.children.find(
          (obj) => obj.uuid === overlayUuid
        ) as THREE.Mesh;
        if (overlay) {
          // Dispose of the old overlay geometry
          if (overlay.geometry) {
            overlay.geometry.dispose();
          }
          // Make sure the geometry has the necessary attributes for cloning and updating
          const newGeometry = updates.geometry.clone();
          if (
            newGeometry.attributes.position &&
            newGeometry.attributes.normal &&
            newGeometry.attributes.uv
          ) {
            newGeometry.computeVertexNormals();
            newGeometry.attributes.position.needsUpdate = true;
            newGeometry.attributes.normal.needsUpdate = true;
            newGeometry.attributes.uv.needsUpdate = true;
            overlay.geometry = newGeometry;
            overlay.position.copy(mesh.position);
            overlay.position.y += 0.001; // Maintain slight elevation
          } else {
            console.warn(
              'New geometry is missing required attributes (position, normal, or uv). Overlay not updated.'
            );
          }
        }
      }
      // Update UI state if the updated mesh is selected
      if (
        selectedMeshesForUI.some(
          (selectedMesh) => selectedMesh.uuid === mesh.uuid
        )
      ) {
        if (mesh.userData.isCube) {
          setCubeSize({
            width: mesh.userData.width,
            height: mesh.userData.height,
            depth: mesh.userData.depth,
          });
        } else if (mesh.userData.isSphere) {
          setSphereRadius(mesh.userData.radius);
        }
      }
    },
    [refs.scene, selectedMeshesForUI, setCubeSize, setSphereRadius]
  );

  const getMenuItemsForMesh = useCallback(
    (mesh: THREE.Mesh): { items: MenuItem[]; title: string } => {
      const items: MenuItem[] = [];
      let title = 'Object';
      console.log('mesh', mesh);
      // cube menu items
      if (mesh.userData.isCube) {
        title = 'Cube';
        items.push({
          label: 'Change Size',
          action: () => {
            Modal.confirm({
              title: 'Change Cube Size',
              content: (
                <div>
                  <Input
                    addonBefore="Width"
                    id="newWidth"
                    type="number"
                    defaultValue={mesh.userData.width || 0.2}
                    step={0.1}
                    min={0.1}
                  />
                  <Input
                    addonBefore="Height"
                    id="newHeight"
                    type="number"
                    defaultValue={mesh.userData.height || 0.2}
                    step={0.1}
                    style={{ marginTop: '8px' }}
                    min={0.1}
                  />
                  <Input
                    addonBefore="Depth"
                    id="newDepth"
                    type="number"
                    defaultValue={mesh.userData.depth || 0.2}
                    step={0.1}
                    style={{ marginTop: '8px' }}
                    min={0.1}
                  />
                </div>
              ),
              onOk: () => {
                const newWidth = parseFloat(
                  (document.getElementById('newWidth') as HTMLInputElement)
                    ?.value || '0.2'
                );
                const newHeight = parseFloat(
                  (document.getElementById('newHeight') as HTMLInputElement)
                    ?.value || '0.2'
                );
                const newDepth = parseFloat(
                  (document.getElementById('newDepth') as HTMLInputElement)
                    ?.value || '0.2'
                );

                if (
                  isNaN(newWidth) ||
                  newWidth <= 0 ||
                  isNaN(newHeight) ||
                  newHeight <= 0 ||
                  isNaN(newDepth) ||
                  newDepth <= 0
                ) {
                  message.error(
                    'Invalid dimensions. All dimensions must be positive numbers.'
                  );
                  return;
                }

                const updates = {
                  geometry: new THREE.BoxGeometry(
                    newWidth,
                    newHeight,
                    newDepth
                  ),
                  userData: {
                    ...mesh.userData,
                    width: newWidth,
                    height: newHeight,
                    depth: newDepth,
                  },
                };
                handleUpdateMesh(mesh, updates);
              },
            });
          },
        });

        items.push({
          label: 'Change Color',
          action: () => {
            let newColor = mesh.userData.originalColor || '#ffffff'; // Default to current or white
            Modal.confirm({
              title: 'Change Cube Color',
              content: (
                <Input
                  type="color"
                  id="newColor"
                  defaultValue={newColor}
                  onChange={(e) => (newColor = e.target.value)}
                />
              ),
              onOk: () => {
                if (
                  !THREE.Color.NAMES[newColor] &&
                  !/^#[0-9A-F]{6}$/i.test(newColor)
                ) {
                  try {
                    new THREE.Color(newColor);
                  } catch (error) {
                    message.error(
                      'Invalid color format. Please use a valid hex color (e.g., #RRGGBB).'
                    );
                    return;
                  }
                }

                meshFactory.updateMeshColor(mesh, newColor); // Update the actual mesh material

                const updates = {
                  geometry: mesh.geometry, // Keep existing geometry
                  userData: {
                    ...mesh.userData,
                    originalColor: newColor, // Store the new color in userData
                  },
                };
                handleUpdateMesh(mesh, updates);
              },
            });
          },
        });
      }

      // sphere menu items
      if (mesh.userData.isSphere) {
        title = 'Sphere';
        items.push({
          label: 'Change Size',
          action: () => {
            Modal.confirm({
              title: 'Change Sphere Size',
              content: (
                <div>
                  <Input
                    addonBefore="Radius"
                    id="newRadius"
                    type="number"
                    defaultValue={mesh.userData.radius || 0.2}
                    step={0.1}
                    min={0.1}
                  />
                </div>
              ),
              onOk: () => {
                const newRadius = parseFloat(
                  (document.getElementById('newRadius') as HTMLInputElement)
                    ?.value || '0.2'
                );

                if (isNaN(newRadius) || newRadius <= 0) {
                  message.error(
                    'Invalid radius. Radius must be a positive number.'
                  );
                  return;
                }

                const updates = {
                  geometry: new THREE.SphereGeometry(newRadius, 32, 32),
                  userData: {
                    ...mesh.userData,
                    radius: newRadius,
                  },
                };
                handleUpdateMesh(mesh, updates);
              },
            });
          },
        });

        items.push({
          label: 'Change Color',
          action: () => {
            let newColor = mesh.userData.originalColor || '#ffffff'; // Default to current or white
            Modal.confirm({
              title: 'Change Sphere Color',
              content: (
                <Input
                  type="color"
                  id="newColor"
                  defaultValue={newColor}
                  onChange={(e) => (newColor = e.target.value)}
                />
              ),
              onOk: () => {
                if (
                  !THREE.Color.NAMES[newColor] &&
                  !/^#[0-9A-F]{6}$/i.test(newColor)
                ) {
                  try {
                    new THREE.Color(newColor);
                  } catch (error) {
                    message.error(
                      'Invalid color format. Please use a valid hex color (e.g., #RRGGBB).'
                    );
                    return;
                  }
                }

                meshFactory.updateMeshColor(mesh, newColor);

                const updates = {
                  geometry: mesh.geometry,
                  userData: {
                    ...mesh.userData,
                    originalColor: newColor,
                  },
                };
                handleUpdateMesh(mesh, updates);
              },
            });
          },
        });
      }

      // Change from direct includes check to safe check
      if (mesh.userData.name?.includes('Walmart Texture GLB')) {
        title = mesh.userData.name;
        items.push({
          label: 'Clone Left',
          action: () => {
            const leftMesh = meshFactory.cloneDirectionalMesh(mesh, 'left');
            refs.scene.current?.add(leftMesh);
            // Clear any existing selections
            clearSelections();
            // Select the new mesh
            refs.selectedMeshes.current = [leftMesh];
            setSelectedMeshesForUI([leftMesh]);
            // Add selection overlay to the new mesh
            if (refs.scene.current) {
              addSelectionOverlay(
                leftMesh,
                refs.scene.current,
                SELECTION_COLOR,
                SELECTION_OPACITY
              );
            }
          },
        });

        items.push({
          label: 'Clone Right',
          action: () => {
            const rightMesh = meshFactory.cloneDirectionalMesh(mesh, 'right');
            refs.scene.current?.add(rightMesh);
            // Clear any existing selections
            clearSelections();
            // Select the new mesh
            refs.selectedMeshes.current = [rightMesh];
            setSelectedMeshesForUI([rightMesh]);
            // Add selection overlay to the new mesh
            if (refs.scene.current) {
              addSelectionOverlay(
                rightMesh,
                refs.scene.current,
                SELECTION_COLOR,
                SELECTION_OPACITY
              );
            }
          },
        });
      }

      // shopper menu items
      if (mesh.userData.isQueueLineShopper) {
        title = 'Shopper';
        items.push({
          label: 'Change Type',
          action: () => {},
        });
      }

      return { items, title };
    },
    [handleUpdateMesh]
  );

  const handleAlignAdjacent = useCallback(() => {
    alignAdjacent(refs);
    snappingService.updateAlignmentLines(refs.scene.current!);
  }, [refs]);

  const handleAlignAdjacentVertical = useCallback(() => {
    alignAdjacentVertical(refs);
    snappingService.updateAlignmentLines(refs.scene.current!);
  }, [refs]);

  const getMenuItemsForMultipleMeshes = useCallback(
    (meshes: THREE.Mesh[]): { items: MenuItem[]; title: string } => {
      const items: MenuItem[] = [];
      const title = `Multiple Objects (${meshes.length})`;

      items.push({
        label: 'Align Adjacent (Horizontal)',
        action: () => {
          handleAlignAdjacent();
        },
      });

      items.push({
        label: 'Align Adjacent (Vertical)',
        action: () => {
          handleAlignAdjacentVertical();
        },
      });

      items.push({
        label: 'Align Horizontal Centers',
        action: () => {
          alignHorizontally(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      items.push({
        label: 'Align Vertical Centers',
        action: () => {
          alignVertically(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      items.push({
        label: 'Align Left',
        action: () => {
          alignLeft(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      items.push({
        label: 'Align Right',
        action: () => {
          alignRight(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      items.push({
        label: 'Align Top',
        action: () => {
          alignTop(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      items.push({
        label: 'Align Bottom',
        action: () => {
          alignBottom(refs);
          snappingService.updateAlignmentLines(refs.scene.current!);
        },
      });

      return { items, title };
    },
    [handleAlignAdjacent, handleAlignAdjacentVertical, refs]
  );

  const { contextMenuState, handleCloseContextMenu } = useContextMenu({
    mountRef,
    refs,
    getMenuItemsForMesh,
    getMenuItemsForMultipleMeshes,
    isCameraControlsEnabled,
  });

  // Initialize queue lines
  useEffect(() => {
    setQueueLines(queueLineManager.initializeQueueLines());
  }, []);

  const handleToggleQueueLineMode = useCallback(() => {
    setIsInQueueLineMode((prevMode) => {
      const nextMode = !prevMode;

      if (nextMode) {
        const availableQueueLine =
          queueLineManager.getAvailableQueueLine(queueLines);
        setCurrentQueueLine(availableQueueLine);

        // Reset all selections when entering queue line mode
        const scene = refs.scene.current;
        if (scene) {
          refs.selectedMeshes.current.forEach((mesh) => {
            removeSelectionOverlay(mesh, scene);
          });
          refs.selectedMeshes.current = [];
          setSelectedMeshesForUI([]);
        }
      } else {
        setCurrentQueueLine(null);

        // Update queueLines with the current queue line's shoppers
        if (currentQueueLine) {
          setQueueLines((prevLines) =>
            prevLines.map((line) =>
              line.lineId === currentQueueLine.lineId ? currentQueueLine : line
            )
          );
        }

        // Check for overlaps when exiting queue line mode
        if (refs.scene.current) {
          debouncedCheckOverlaps(refs.scene.current);
        }
      }

      return nextMode;
    });
  }, [
    queueLines,
    refs.scene,
    refs.selectedMeshes,
    currentQueueLine,
    debouncedCheckOverlaps,
  ]);

  const handleSelectShopperType = useCallback((type: ShopperType) => {
    setSelectedShopperType(type);
  }, []);

  const handleQueueLineModeClick = useCallback(
    (event: MouseEvent) => {
      if (
        !isInQueueLineMode ||
        !currentQueueLine ||
        !refs.scene.current ||
        !refs.camera.current
      ) {
        return;
      }

      // Get click position in normalized device coordinates
      const rect = mountRef.current?.getBoundingClientRect();
      if (!rect) return;

      const normalizedX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const normalizedY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Raycasting to find intersection with floor
      const raycaster = refs.raycaster.current;
      raycaster.setFromCamera(
        { x: normalizedX, y: normalizedY },
        refs.camera.current
      );

      const floorPlane = refs.floorPlane.current;
      if (!floorPlane) return;

      const intersects = raycaster.intersectObject(floorPlane);
      if (intersects.length === 0) return;

      // Create shopper at intersection point
      const position = intersects[0].point;
      const shopperMesh = queueLineManager.addShopperToQueueLine(
        refs.scene.current,
        position,
        currentQueueLine,
        selectedShopperType
      );

      if (shopperMesh) {
        // Update currentQueueLine with the new shopper
        setCurrentQueueLine((prevLine) => {
          if (!prevLine) return null;

          return {
            ...prevLine,
            shoppers: [...prevLine.shoppers, shopperMesh],
          };
        });

        // update queue lines state
        setQueueLines((prevLines) =>
          prevLines.map((line) =>
            line.lineId === currentQueueLine.lineId
              ? {
                  ...line,
                  shoppers: [...line.shoppers, shopperMesh],
                }
              : line
          )
        );

        // Check for overlapping shoppers
        debouncedCheckOverlaps(refs.scene.current);
      }
    },
    [
      isInQueueLineMode,
      currentQueueLine,
      refs.scene,
      refs.camera,
      refs.raycaster,
      refs.floorPlane,
      selectedShopperType,
      debouncedCheckOverlaps,
    ]
  );

  // Add and remove click handler when in queue line mode
  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) return;

    if (isInQueueLineMode) {
      mountElement.addEventListener('click', handleQueueLineModeClick);
    }

    return () => {
      mountElement.removeEventListener('click', handleQueueLineModeClick);
    };
  }, [isInQueueLineMode, handleQueueLineModeClick]);

  const handleUpdateShopper = useCallback(
    (
      shopper: ShopperMesh,
      action: 'updateShopperType' | 'removeShopper' | 'updateElapsedWaitTime',
      value?: ShopperType | string
    ): void => {
      if (!refs.scene.current) return;

      const currentLineId = shopper.userData.lineId;

      switch (action) {
        case 'updateShopperType':
          if (typeof value === 'number') {
            const originalShopperId = shopper.id;
            queueLineManager.updateShopperType(
              refs.scene.current,
              shopper,
              value
            );

            setQueueLines((prevLines) => {
              const newLines = [...prevLines];
              const lineIndex = newLines.findIndex(
                (line) => line.lineId === shopper.userData.lineId
              );
              if (lineIndex >= 0) {
                const updatedShoppers = refs.scene.current!.children.filter(
                  (obj): obj is ShopperMesh =>
                    obj instanceof THREE.Mesh &&
                    obj.userData.isQueueLineShopper &&
                    obj.userData.lineId === shopper.userData.lineId
                );
                newLines[lineIndex].shoppers = updatedShoppers;
              }
              return newLines;
            });

            const newShopper = refs.scene.current!.children.find(
              (obj): obj is ShopperMesh =>
                obj instanceof THREE.Mesh &&
                obj.userData.isQueueLineShopper &&
                obj.userData.lineId === currentLineId &&
                obj.id !== originalShopperId
            );

            if (newShopper && newShopper.userData.hasOverlay) {
              setSelectedMeshesForUI([newShopper]);
              refs.selectedMeshes.current = [newShopper];
            }
          }
          break;

        case 'removeShopper':
          setQueueLines((prevLines) => {
            const newLines = [...prevLines];
            const allDeleted = queueLineManager.removeShopper(
              refs.scene.current!,
              shopper,
              newLines
            );

            if (allDeleted) {
              cleanupAllSelectionOverlays();
              const resetLines = queueLineManager.initializeQueueLines();
              refs.queueLines.current = resetLines;
              return resetLines;
            }

            const lineIndex = newLines.findIndex(
              (line) => line.lineId === shopper.userData.lineId
            );
            if (lineIndex >= 0) {
              const updatedShoppers = refs.scene.current!.children.filter(
                (obj): obj is ShopperMesh =>
                  obj instanceof THREE.Mesh &&
                  obj.userData.isQueueLineShopper &&
                  obj.userData.lineId === shopper.userData.lineId
              );
              newLines[lineIndex].shoppers = updatedShoppers;
            }

            refs.queueLines.current = newLines;
            return newLines;
          });

          if (!queueLineManager.areAllShoppersDeleted(refs.scene.current)) {
            const nextShopper = refs.scene.current.children.find(
              (obj): obj is ShopperMesh =>
                obj instanceof THREE.Mesh &&
                obj.userData.isQueueLineShopper &&
                obj.userData.lineId === currentLineId &&
                obj !== shopper
            );

            if (nextShopper) {
              refs.selectedMeshes.current = [nextShopper];
              setSelectedMeshesForUI([nextShopper]);
              addSelectionOverlay(
                nextShopper,
                refs.scene.current,
                SELECTION_COLOR,
                SELECTION_OPACITY
              );
              updateShopperBadgeSelection(nextShopper, true);
            } else {
              refs.selectedMeshes.current = [];
              setSelectedMeshesForUI([]);
            }
          }
          break;

        case 'updateElapsedWaitTime':
          if (typeof value === 'string') {
            queueLineManager.updateShopperWaitTime(shopper, value);
          }
          break;
      }

      // Check for overlaps after any shopper update
      if (refs.scene.current) {
        debouncedCheckOverlaps(refs.scene.current);
      }
    },
    [
      refs.scene,
      refs.selectedMeshes,
      cleanupAllSelectionOverlays,
      debouncedCheckOverlaps,
    ]
  );

  // Update selectedQueueLine when selection changes
  useEffect(() => {
    if (
      selectedMeshesForUI.length === 1 &&
      selectedMeshesForUI[0].userData.isQueueLineShopper
    ) {
      const shopper = selectedMeshesForUI[0] as ShopperMesh;
      const queueLine = queueLineManager.getShopperQueueLine(
        queueLines,
        shopper
      );
      setSelectedQueueLine(queueLine || null);
    } else {
      setSelectedQueueLine(null);
    }
  }, [selectedMeshesForUI, queueLines]);

  // Show camera controls modal on initial load
  useEffect(() => {
    // Check if we should show the modal based on localStorage
    const shouldShowModal =
      localStorage.getItem('hideCameraControlsModal') !== 'true';
    if (shouldShowModal) {
      setShowCameraControlsModal(true);
    }
  }, []);

  // Function to toggle camera controls
  const toggleCameraControls = useCallback(() => {
    setIsCameraControlsEnabled((prev) => !prev);
  }, []);

  const handleModalClose = () => {
    setShowCameraControlsModal(false);

    // If "Don't show again" is checked, save preference to localStorage
    if (dontShowAgain) {
      localStorage.setItem('hideCameraControlsModal', 'true');
    }
  };

  const animate = useCallback(() => {
    if (!refs.scene.current || !refs.camera.current || !refs.renderer.current) {
      return;
    }

    refs.renderer.current.render(refs.scene.current, refs.camera.current);

    // Check for overlapping shoppers if we're in queue line mode
    if (isInQueueLineMode) {
      debouncedCheckOverlaps(refs.scene.current);
    }

    requestAnimationFrame(animate);
  }, [
    refs.scene,
    refs.camera,
    refs.renderer,
    isInQueueLineMode,
    debouncedCheckOverlaps,
  ]);

  // Add the selection box hook after other hooks
  const { isSelecting, selectionBox } = useSelectionBox({
    camera:
      refs.camera as React.MutableRefObject<THREE.PerspectiveCamera | null>,
    mountRef,
    scene: refs.scene,
    onUpdateSelection: (meshes) => {
      refs.selectedMeshes.current = meshes;
      setSelectedMeshesForUI(meshes);

      // Use existing overlay functions
      cleanupAllSelectionOverlays();
      meshes.forEach((mesh) =>
        addSelectionOverlay(
          mesh,
          refs.scene.current!,
          SELECTION_COLOR,
          SELECTION_OPACITY
        )
      );
    },
    isCameraControlsEnabled,
  });

  // Add snapping configuration state
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(true);
  const [isGridSnappingEnabled, setIsGridSnappingEnabled] = useState(true);
  const [isEdgeSnappingEnabled, setIsEdgeSnappingEnabled] = useState(true);
  const [isRotationSnappingEnabled, setIsRotationSnappingEnabled] =
    useState(true);
  const [gridSize, setGridSize] = useState(0.25);
  const [snapThreshold, setSnapThreshold] = useState(0.2);
  const [rotationSnapAngle, setRotationSnapAngle] = useState(45);

  // Collision group state
  const [isUseCollisionGroups, setIsUseCollisionGroups] = useState(false);

  // Performance optimization state
  const [isSpatialGridEnabled, setIsSpatialGridEnabled] = useState(true);
  const [spatialGridCellSize, setSpatialGridCellSize] = useState(5.0);
  const [isLoDEnabled, setIsLoDEnabled] = useState(true);
  const [isFrustumCullingEnabled, setIsFrustumCullingEnabled] = useState(true);
  const [maxObjectsToCheck, setMaxObjectsToCheck] = useState(50);
  const [showDebugVisualization, setShowDebugVisualization] = useState(false);
  const [showPerformancePanel, setShowPerformancePanel] = useState(false);

  // Create a debounced function for updating the spatial grid
  const debouncedUpdateSpatialGrid = useMemo(
    () =>
      debounce(() => {
        if (refs.scene.current && isSpatialGridEnabled) {
          snappingService.updateSpatialGrid(
            refs.scene.current,
            refs.floorPlane.current || undefined
          );

          console.log('[Performance] Spatial grid updated');
        }
      }, 500), // 500ms debounce
    [refs.scene, refs.floorPlane, isSpatialGridEnabled]
  );

  // Hook into the scene's add/remove methods to detect changes
  useEffect(() => {
    if (!refs.scene.current || !isSpatialGridEnabled) return;

    const scene = refs.scene.current;

    // Store the original add/remove methods
    const originalAdd = scene.add;
    const originalRemove = scene.remove;

    // Override the add method to update the spatial grid when objects are added
    scene.add = function (this: THREE.Scene, ...objects: THREE.Object3D[]) {
      const result = originalAdd.apply(this, objects);
      debouncedUpdateSpatialGrid();
      return result;
    } as typeof scene.add;

    // Override the remove method to update the spatial grid when objects are removed
    scene.remove = function (this: THREE.Scene, ...objects: THREE.Object3D[]) {
      const result = originalRemove.apply(this, objects);
      debouncedUpdateSpatialGrid();
      return result;
    } as typeof scene.remove;

    // Initial update
    debouncedUpdateSpatialGrid();

    // Cleanup on unmount
    return () => {
      if (refs.scene.current) {
        refs.scene.current.add = originalAdd;
        refs.scene.current.remove = originalRemove;
      }
    };
  }, [refs.scene, isSpatialGridEnabled, debouncedUpdateSpatialGrid]);

  return (
    <>
      <header className="feb-view-header">
        <Space direction="horizontal" size="middle">
          <BackArrowIcon
            onClick={() => {
              dispatch(clearCurrentStoreBuild());
              history.push(RoutePath.FrontEndBuilder);
            }}
            style={{ fontSize: '18px', marginTop: '6px', cursor: 'pointer' }}
          />
          <Title level={4}>{build?.name}</Title>
        </Space>
      </header>
      <div className="feb-view">
        {!build || !environment ? (
          <div className="feb-error-message">
            No build data available. Please select a build from the Store
            Builder page.
          </div>
        ) : (
          <>
            <Toolbar
              refs={refs}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onZoomToFit={handleZoomToFit}
              onResetCamera={handleResetCamera}
              onAlignHorizontally={() => alignHorizontally(refs)}
              onAlignVertically={() => alignVertically(refs)}
              onAlignLeft={() => alignLeft(refs)}
              onAlignRight={() => alignRight(refs)}
              onAlignTop={() => alignTop(refs)}
              onAlignBottom={() => alignBottom(refs)}
              onRotate={handleRotate}
              onTakeScreenshot={handleTakeScreenshot}
              onAddText={handleAddText}
              onAddRectangle={handleAddRectangle}
              onAddCircle={handleAddCircle}
              onDownloadRSF={() => handleDownloadRSF(build.uuid)}
              isCameraControlsEnabled={isCameraControlsEnabled}
              onToggleCameraControls={toggleCameraControls}
              onToggleQueueLineMode={handleToggleQueueLineMode}
              onSelectShopperType={handleSelectShopperType}
              isInQueueLineMode={isInQueueLineMode}
              disableQueueLineMode={false}
              selectedShopperType={selectedShopperType}
              currentQueueLineColor={currentQueueLine?.color}
              onTogglePerformancePanel={() =>
                setShowPerformancePanel((prev) => !prev)
              }
              onAlignAdjacent={handleAlignAdjacent}
              onAlignAdjacentVertical={handleAlignAdjacentVertical}
            />
            <div ref={mountRef} className="feb-canvas">
              <input
                ref={textInputRef}
                type="text"
                onBlur={handleTextInputBlur}
                onKeyPress={handleTextInputKeyPress}
              />
              {(() => {
                console.log(
                  'FebView - isSelecting:',
                  isSelecting,
                  'selectionBox:',
                  selectionBox
                );
                return null;
              })()}
              <SelectionBoxOverlay
                isVisible={isSelecting}
                selectionBox={selectionBox}
              />
              {isDebugMode && (
                <SnappingDebugPanel
                  snappingService={snappingService}
                  scene={refs.scene}
                  floorPlane={refs.floorPlane}
                  isSnappingEnabled={isSnappingEnabled}
                  setIsSnappingEnabled={setIsSnappingEnabled}
                  isGridSnappingEnabled={isGridSnappingEnabled}
                  setIsGridSnappingEnabled={setIsGridSnappingEnabled}
                  isEdgeSnappingEnabled={isEdgeSnappingEnabled}
                  setIsEdgeSnappingEnabled={setIsEdgeSnappingEnabled}
                  gridSize={gridSize}
                  setGridSize={setGridSize}
                  snapThreshold={snapThreshold}
                  setSnapThreshold={setSnapThreshold}
                  isRotationSnappingEnabled={isRotationSnappingEnabled}
                  setIsRotationSnappingEnabled={setIsRotationSnappingEnabled}
                  rotationSnapAngle={rotationSnapAngle}
                  setRotationSnapAngle={setRotationSnapAngle}
                  isUseCollisionGroups={isUseCollisionGroups}
                  setIsUseCollisionGroups={setIsUseCollisionGroups}
                />
              )}

              {showPerformancePanel && (
                <PerformanceDebugPanel
                  snappingService={snappingService}
                  scene={refs.scene}
                  renderer={refs.renderer}
                  isSpatialGridEnabled={isSpatialGridEnabled}
                  setIsSpatialGridEnabled={setIsSpatialGridEnabled}
                  spatialGridCellSize={spatialGridCellSize}
                  setSpatialGridCellSize={setSpatialGridCellSize}
                  isLoDEnabled={isLoDEnabled}
                  setIsLoDEnabled={setIsLoDEnabled}
                  isFrustumCullingEnabled={isFrustumCullingEnabled}
                  setIsFrustumCullingEnabled={setIsFrustumCullingEnabled}
                  maxObjectsToCheck={maxObjectsToCheck}
                  setMaxObjectsToCheck={setMaxObjectsToCheck}
                  showDebugVisualization={showDebugVisualization}
                  setShowDebugVisualization={setShowDebugVisualization}
                />
              )}
            </div>
            <SidePanel
              onDragFixture={(item) => {
                setDraggedItem(item);
              }}
              selectedMeshes={selectedMeshesForUI}
              onUpdateMesh={handleUpdateMesh}
              selectedQueueLine={selectedQueueLine || undefined}
              onUpdateShopper={handleUpdateShopper}
              overlappingShoppers={overlappingShoppers}
              isGridVisible={isGridVisible}
              isRendered={currentRenderType === 'rendered'}
              onToggleGrid={toggleGridVisibility}
              onToggleRenderType={toggleRenderType}
              cubeSize={cubeSize}
              sphereRadius={sphereRadius}
              handleUpdateCubeSize={handleUpdateMesh}
              handleUpdateSphereRadius={handleUpdateMesh}
              handleUpdateColor={handleUpdateMesh}
            />

            {/* Camera Controls Instructions Modal */}
            <Modal
              title={
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  Camera Controls Instructions
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ position: 'relative', top: '0px' }}
                  >
                    <path d="M16.466 7.5C15.643 4.237 13.952 2 12 2 9.239 2 7 6.477 7 12s2.239 10 5 10c.342 0 .677-.069 1-.2" />
                    <path d="m15.194 13.707 3.814 1.86-1.86 3.814" />
                    <path d="M19 15.57c-1.804.885-4.274 1.43-7 1.43-5.523 0-10-2.239-10-5s4.477-5 10-5c4.838 0 8.873 1.718 9.8 4" />
                  </svg>
                </div>
              }
              open={showCameraControlsModal}
              onOk={handleModalClose}
              onCancel={handleModalClose}
              okText="Got it!"
              cancelButtonProps={{ style: { display: 'none' } }}
            >
              <Typography.Paragraph>
                You can control the camera using the following methods:
              </Typography.Paragraph>
              <ul>
                <li>
                  Rotate the camera with the <strong>left mouse button</strong>
                </li>
                <li>
                  Pan the camera with the <strong>right mouse button</strong>
                </li>
                <li>
                  Pan by holding down the <strong>Shift key</strong> while
                  dragging with the left mouse button
                </li>
                <li>
                  Zoom in and out with the <strong>mouse wheel</strong>
                </li>
              </ul>
              <Typography.Paragraph>
                <strong>Enable camera controls in the toolbar.</strong> Disable
                camera controls to return to normal object placement mode.
              </Typography.Paragraph>

              <div style={{ marginTop: 16 }}>
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                >
                  Don't show this message again
                </Checkbox>
              </div>
            </Modal>
            <ContextMenu
              isVisible={contextMenuState.isVisible}
              position={contextMenuState.position}
              menuItems={contextMenuState.menuItems}
              onClose={handleCloseContextMenu}
              title={contextMenuState.title}
            />
          </>
        )}
      </div>
    </>
  );
};

export default FebView;
